import { useState, useEffect, useCallback } from 'react';
import { milestonesAPI, uploadFile, deleteAttachment } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { TaskAttachments } from './TaskAttachments';
import { TaskComments } from './TaskComments';

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started', color: '#DC2626' },
  { value: 'in_progress', label: 'In Progress', color: '#D97706' },
  { value: 'completed', label: 'Completed', color: '#059669' },
  { value: 'testing', label: 'Testing', color: '#2563EB' },
  { value: 'blocked', label: 'Blocked', color: '#1F2937' },
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High', color: '#DC2626' },
  { value: 'medium', label: 'Medium', color: '#F59E0B' },
  { value: 'low', label: 'Low', color: '#22C55E' },
];

const EMPTY_FORM = {
  title: '', description: '', module_id: '', status: 'not_started',
  priority: 'medium', assignee_ids: [], start_date: '', end_date: '',
  estimated_hours: 0, actual_hours: 0, story_points: 0,
};

function ColoredSelect({ label, value, options, onChange, disabled, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />{opt.label}</div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function AssigneesField({ formData, setFormData, canEdit, users, taskData }) {
  const developers = users.filter(u => u.role === 'developer');

  if (!canEdit) {
    if (!taskData?.assignees?.length) return null;
    return (
      <div>
        <Label>Assigned Developers</Label>
        <div className="flex flex-wrap gap-1 mt-1">{taskData.assignees.map(a => <Badge key={a.id} variant="secondary">{a.name}</Badge>)}</div>
      </div>
    );
  }

  return (
    <div>
      <Label>Assigned Developers</Label>
      <div className="border border-[#E0E0E0] rounded-md p-2 space-y-1 max-h-32 overflow-y-auto">
        {developers.map(dev => (
          <label key={dev.id} className="flex items-center gap-2 px-2 py-1 hover:bg-[#F5F5F5] rounded cursor-pointer text-sm">
            <input type="checkbox" checked={formData.assignee_ids?.includes(dev.id)}
              onChange={(e) => setFormData(f => ({
                ...f, assignee_ids: e.target.checked ? [...(f.assignee_ids || []), dev.id] : (f.assignee_ids || []).filter(id => id !== dev.id)
              }))} className="rounded" />
            {dev.name}
          </label>
        ))}
        {developers.length === 0 && <p className="text-xs text-[#999999] p-1">No developers available</p>}
      </div>
    </div>
  );
}

function MilestoneDetailsFields({ formData, setFormData, canEdit, isDeveloper, modules, users, taskData }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Description</Label>
        {canEdit ? (
          <Textarea value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} rows={4} data-testid="task-description" />
        ) : (
          <p className="text-sm text-[#666666] mt-1">{formData.description || 'No description'}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <ColoredSelect label="Status" value={formData.status} options={STATUS_OPTIONS}
          onChange={v => setFormData(f => ({ ...f, status: v }))} disabled={!canEdit && !isDeveloper} testId="task-status" />
        <ColoredSelect label="Priority" value={formData.priority} options={PRIORITY_OPTIONS}
          onChange={v => setFormData(f => ({ ...f, priority: v }))} disabled={!canEdit} testId="task-priority" />
      </div>

      <div>
        <Label>Module</Label>
        <Select value={formData.module_id} onValueChange={v => setFormData(f => ({ ...f, module_id: v }))} disabled={!canEdit}>
          <SelectTrigger data-testid="task-module"><SelectValue placeholder="Select module" /></SelectTrigger>
          <SelectContent>{modules.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <AssigneesField formData={formData} setFormData={setFormData} canEdit={canEdit} users={users} taskData={taskData} />
    </div>
  );
}

function MilestoneScheduleFields({ formData, setFormData, canEdit, canEditHours, taskData }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Start Date</Label><Input type="date" value={formData.start_date} onChange={e => setFormData(f => ({ ...f, start_date: e.target.value }))} disabled={!canEdit} data-testid="task-start-date" /></div>
        <div><Label>End Date</Label><Input type="date" value={formData.end_date} onChange={e => setFormData(f => ({ ...f, end_date: e.target.value }))} disabled={!canEdit} data-testid="task-end-date" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Estimated Hours</Label><Input type="number" value={formData.estimated_hours} onChange={e => setFormData(f => ({ ...f, estimated_hours: parseFloat(e.target.value) || 0 }))} disabled={!canEdit} data-testid="task-est-hours" /></div>
        <div><Label>Actual Hours</Label><Input type="number" value={formData.actual_hours} onChange={e => setFormData(f => ({ ...f, actual_hours: parseFloat(e.target.value) || 0 }))} disabled={!canEditHours} data-testid="task-actual-hours" /></div>
      </div>
      <div><Label>Story Points</Label><Input type="number" value={formData.story_points} onChange={e => setFormData(f => ({ ...f, story_points: parseInt(e.target.value) || 0 }))} disabled={!canEdit} data-testid="task-story-points" /></div>

      {(taskData?.restaurant_name || taskData?.idea_title) && (
        <div className="bg-[#F5F5F5] rounded-lg p-3">
          <Label className="text-xs text-[#666666]">Linked Items</Label>
          {taskData?.restaurant_name && <p className="text-sm mt-1">Restaurant: <span className="font-medium">{taskData.restaurant_name}</span></p>}
          {taskData?.idea_title && <p className="text-sm mt-1">Idea: <span className="font-medium">{taskData.idea_title}</span></p>}
        </div>
      )}
    </div>
  );
}

export default function MilestoneModal({ task, modules, users = [], onClose, onUpdate, isAdmin, isDeveloper }) {
  const [loading, setLoading] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);

  const fetchTaskDetails = useCallback(async (overwriteForm = true) => {
    try {
      const res = await milestonesAPI.getOne(task.id);
      setTaskData(res.data);
      if (overwriteForm) {
        setFormData({
          title: res.data.title || '', description: res.data.description || '',
          module_id: res.data.module_id || '', status: res.data.status || 'not_started',
          priority: res.data.priority || 'medium', assignee_ids: res.data.assignee_ids || [],
          start_date: res.data.start_date || '', end_date: res.data.end_date || '',
          estimated_hours: res.data.estimated_hours || 0, actual_hours: res.data.actual_hours || 0,
          story_points: res.data.story_points || 0,
        });
      }
    } catch (err) {
      toast.error('Failed to load milestone details');
    }
  }, [task?.id]);

  useEffect(() => {
    if (task?.id) {
      fetchTaskDetails(true);
    } else {
      setTaskData(null);
      setFormData({
        ...EMPTY_FORM,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
      });
    }
  }, [task?.id, fetchTaskDetails]);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (task?.id) {
        await milestonesAPI.update(task.id, formData);
        toast.success('Milestone updated');
      } else {
        await milestonesAPI.create(formData);
        toast.success('Milestone created');
      }
      onUpdate();
    } catch (err) {
      toast.error('Failed to save milestone');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this milestone?')) return;
    setLoading(true);
    try {
      await milestonesAPI.delete(task.id);
      toast.success('Milestone deleted');
      onUpdate();
    } catch (err) {
      toast.error('Failed to delete task');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (content) => {
    try {
      await milestonesAPI.addComment(task.id, content);
      fetchTaskDetails(false);
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is too large (max 10MB)`); continue; }
        await uploadFile(file, task.id);
      }
      fetchTaskDetails(false);
      toast.success('Files uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteAttachment(attachmentId);
      fetchTaskDetails(false);
      toast.success('File deleted');
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const canEdit = isAdmin;
  const canEditHours = isAdmin || isDeveloper;
  const canComment = isAdmin || isDeveloper;
  const canUpload = isAdmin || isDeveloper;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-[800px] max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="task-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E0E0E0]">
          <div className="flex items-center gap-3">
            {canEdit ? (
              <Input value={formData.title} onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                className="text-lg font-semibold border-0 p-0 h-auto focus-visible:ring-0" placeholder="Milestone title" data-testid="task-title-input" />
            ) : (
              <h2 className="text-lg font-semibold text-[#111111]">{formData.title}</h2>
            )}
            {taskData?.module_name && <Badge variant="secondary">{taskData.module_name}</Badge>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={20} /></Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MilestoneDetailsFields formData={formData} setFormData={setFormData} canEdit={canEdit}
              isDeveloper={isDeveloper} modules={modules} users={users} taskData={taskData} />
            <MilestoneScheduleFields formData={formData} setFormData={setFormData} canEdit={canEdit}
              canEditHours={canEditHours} taskData={taskData} />
          </div>

          {task?.id && (
            <TaskAttachments
              attachments={taskData?.attachments || []}
              canUpload={canUpload}
              uploading={uploading}
              onUpload={handleFileUpload}
              onDelete={handleDeleteAttachment}
              isAdmin={isAdmin}
              isDeveloper={isDeveloper}
            />
          )}

          {task?.id && (
            <TaskComments
              comments={taskData?.comments || []}
              canComment={canComment}
              onAddComment={handleAddComment}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-[#E0E0E0]">
          <div>
            {isAdmin && task?.id && (
              <Button variant="destructive" onClick={handleDelete} disabled={loading} data-testid="delete-task-btn">Delete</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="cancel-btn">Cancel</Button>
            {(canEdit || canEditHours) && (
              <Button onClick={handleSave} disabled={loading} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="save-task-btn">
                {loading ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
